// HM-319.r cluster 15.4: 2 info-cards affiancate sotto i KPI nella Dashboard
// SPEC mockup v3 §info-row.
//   - sx: ProssimiArrivi (CalendarClock arancio + lista arrival-row)
//   - dx: StatoSistema (Cpu arancio + 5 driver online/offline)
// Padding 20 + radius 12 + border 1px (piu' aerato del RoomDetailPanel
// drawer-style del cluster 15.5).

import { CalendarClock, Cpu } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import type { BookingDto, RoomDb } from '../../api/rooms.js';
import type { LiveHalAlert } from '../../hooks/useDashboardLiveSync.js';
import { formatTime } from './lib.js';

interface InfoCardsRowProps {
  rooms: RoomDb[];
  bookings: BookingDto[];
  halAlerts: LiveHalAlert[];
  now: Date;
}

export function InfoCardsRow({ rooms, bookings, halAlerts, now }: InfoCardsRowProps) {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-xl:gap-2.5"
      data-testid="info-cards-row"
    >
      <UpcomingArrivalsCard rooms={rooms} bookings={bookings} now={now} />
      <SystemStatusCard halAlerts={halAlerts} />
    </div>
  );
}

// ---------------------------------------------------------------------- Card 1

function UpcomingArrivalsCard({
  rooms,
  bookings,
  now,
}: {
  rooms: RoomDb[];
  bookings: BookingDto[];
  now: Date;
}) {
  const arrivals = bookings
    .filter((b) => b.status === 'confirmed' && new Date(b.startAt).getTime() > now.getTime())
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
    .slice(0, 5);

  const roomCodeById = new Map(rooms.map((r) => [r.id, r.code] as const));
  const oneHourMs = 60 * 60 * 1000;

  return (
    <article
      data-testid="info-upcoming-arrivals"
      className="bg-bg-card border border-border-base rounded-xl p-5 max-xl:p-3"
    >
      <header className="flex items-center gap-2 mb-4 max-xl:mb-2.5">
        <CalendarClock size={18} strokeWidth={1.75} className="text-accent-primary" />
        <h3 className="text-sm font-semibold text-text-primary m-0">Prossimi arrivi</h3>
      </header>
      {arrivals.length === 0 ? (
        <p className="text-[13px] text-text-muted text-center py-5 m-0">Nessun arrivo previsto</p>
      ) : (
        <div>
          {arrivals.map((b, idx) => {
            const start = new Date(b.startAt);
            const soon = start.getTime() - now.getTime() <= oneHourMs;
            return (
              <div
                key={b.id}
                className={cn(
                  'flex justify-between items-center py-2.5 text-xs',
                  idx > 0 && 'border-t border-bg-muted',
                  idx === 0 && 'pt-0',
                  idx === arrivals.length - 1 && 'pb-0',
                )}
              >
                <div className="min-w-0 mr-2">
                  <div className="text-text-primary font-semibold text-[13px] truncate">
                    {b.guestName ?? 'Ospite'}
                  </div>
                  <div className="text-text-muted text-[11px] mt-0.5 truncate">
                    Camera {b.roomId ? (roomCodeById.get(b.roomId) ?? '?') : '?'}
                    {b.service === 'day_use' ? ' · Day Use' : ' · Pernottamento'}
                  </div>
                </div>
                <div
                  className={cn(
                    'font-semibold text-[13px] tabular-nums',
                    soon ? 'text-accent-primary-text' : 'text-text-secondary',
                  )}
                >
                  {formatTime(start)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------- Card 2

const SYSTEM_DRIVERS: Array<{ key: string; aliases: string[]; label: string }> = [
  { key: 'knx', aliases: ['knx'], label: 'KNX bus' },
  { key: 'glory', aliases: ['glory', 'cash'], label: 'Glory cassa' },
  { key: 'epson', aliases: ['epson', 'fiscal'], label: 'Stampante RT' },
  { key: 'revolut', aliases: ['revolut', 'payment'], label: 'Revolut POS' },
  { key: 'whatsapp', aliases: ['whatsapp', 'messaging'], label: 'WhatsApp' },
];

function SystemStatusCard({ halAlerts }: { halAlerts: LiveHalAlert[] }) {
  const offlineByDriver = new Set(
    halAlerts.filter((a) => a.severity === 'error').map((a) => a.driver.toLowerCase()),
  );

  return (
    <article
      data-testid="info-system-status"
      className="bg-bg-card border border-border-base rounded-xl p-5 max-xl:p-3"
    >
      <header className="flex items-center gap-2 mb-4 max-xl:mb-2.5">
        <Cpu size={18} strokeWidth={1.75} className="text-accent-primary" />
        <h3 className="text-sm font-semibold text-text-primary m-0">Stato sistema</h3>
      </header>
      <div>
        {SYSTEM_DRIVERS.map((d, idx) => {
          const offline = d.aliases.some((a) => offlineByDriver.has(a));
          return (
            <div
              key={d.key}
              className={cn(
                'flex justify-between items-center py-2 text-[13px]',
                idx > 0 && 'border-t border-bg-muted',
                idx === 0 && 'pt-0',
                idx === SYSTEM_DRIVERS.length - 1 && 'pb-0',
              )}
              data-driver={d.key}
              data-status={offline ? 'offline' : 'online'}
            >
              <span className="text-text-primary">{d.label}</span>
              <span
                className={cn(
                  'font-semibold flex items-center gap-1.5',
                  offline ? 'text-state-guas' : 'text-[#5F8A50]',
                )}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: offline ? '#A8453A' : '#5F8A50' }}
                />
                {offline ? 'offline' : 'online'}
              </span>
            </div>
          );
        })}
      </div>
    </article>
  );
}
